'use client';

import { useBunja } from 'bunja/react';
import { useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop';

import { T } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import { Modal } from '@flipster/react/design-system/modal';
import { css } from '@flipster/styled-system/css';

import { editProfileBunja } from '../edit-profile/bunja';
import { getCroppedImage } from './crop-image';

import './index.css';

const handleSize = 24;
const handleBorder = 3;

interface Props {
  onClose: () => void;
  imageSrc: string;
  mimeType: string;
  onConfirm: (croppedBlob: Blob, previewUrl: string) => void | Promise<void>;
}

export const AddProfileImageModal = ({ imageSrc, mimeType, onClose, onConfirm }: Props) => {
  const { isOpen, unmount } = useOverlayItem();
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useBunja(editProfileBunja);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, width, height), width, height);
    setCrop(initialCrop);
  };

  const handleConfirm = async () => {
    if (!completedCrop || !imgRef.current) return;
    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImage(imgRef.current, completedCrop, mimeType);
      const previewUrl = URL.createObjectURL(croppedBlob);
      await onConfirm(croppedBlob, previewUrl);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={unmount}>
      <Modal.Header>
        <Modal.Title label={<T id='Copy.r260312.Add_profile_picture' />} />
      </Modal.Header>
      <Modal.Content>
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={1}
          className={css({
            '& .ReactCrop__drag-handle': {
              width: `${handleSize}px`,
              height: `${handleSize}px`,
              border: 'none',
              transform: 'none',
              background: 'none',
            },
            '& .ReactCrop__drag-handle.ord-nw': {
              borderTop: `${handleBorder}px solid #fff`,
              borderLeft: `${handleBorder}px solid #fff`,
            },
            '& .ReactCrop__drag-handle.ord-ne': {
              borderTop: `${handleBorder}px solid #fff`,
              borderRight: `${handleBorder}px solid #fff`,
            },
            '& .ReactCrop__drag-handle.ord-sw': {
              borderBottom: `${handleBorder}px solid #fff`,
              borderLeft: `${handleBorder}px solid #fff`,
            },
            '& .ReactCrop__drag-handle.ord-se': {
              borderBottom: `${handleBorder}px solid #fff`,
              borderRight: `${handleBorder}px solid #fff`,
            },
            '& .ReactCrop__crop-selection': {
              background: 'none',
            },
          })}
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt='Crop target'
            onLoad={onImageLoad}
            className={css({ maxW: '100%', display: 'block' })}
          />
        </ReactCrop>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button
          hierarchy='secondary'
          color='neutral'
          size='large'
          label={<T id='Copy.r260312.Cancel_button' />}
          onClick={onClose}
          css={{ flex: 1 }}
        />
        <Modal.Button
          size='large'
          label={<T id='Copy.r260312.Confirm_button' />}
          onClick={handleConfirm}
          isLoading={isProcessing}
          disabled={!completedCrop}
          css={{ flex: 1 }}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};
